/******************************************************************************
 * 
 * Strauss Motion
 * 
 * Copyright (c) 2022 Robimo GmbH
 *
 * Authors: Daniel Martinek
 *
 *****************************************************************************/
import { ReactElement, useRef, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Gender, InspirationData, LibraryData, StraussLogoType } from '../store/interfaces';
import {ReactComponent as LogoClose}  from '../icons/close.svg'

import {ReactComponent as IconStandard} from '../icons/10_micro_box_no_type_rgb_pos.svg';
import {ReactComponent as IconBlack} from '../icons/10_micro_box_no_type_black_pos.svg';
import {ReactComponent as IconWhite} from '../icons/10_micro_box_no_type_white_border_pos.svg';
import {ReactComponent as ArrowLeft} from '../icons/arrow_left.svg';
import {ReactComponent as ArrowRight} from '../icons/arrow_right.svg';

import {ReactComponent as BackgroundSprite1} from '../icons/color_sprite_bg_1.svg';
import {ReactComponent as BackgroundSprite2} from '../icons/color_sprite_bg_2.svg';

import {ReactComponent as IconFemale} from '../icons/female.svg';
import {ReactComponent as IconMale} from '../icons/male.svg';

import styles from '../styles/inspirationscreen.module.scss'
import { LogoText } from './LogoText';
import { generateBackgroundSplashBaseFill, generateBackgroundSplashContrastFill, generateBackgroundStyle } from '../helpers/helpers';
import { translate } from '../i18n';
import { isMobile } from 'react-device-detect';
import { PreviewContainer, PreviewContainerModel } from './Preview';

 interface InspirationscreenProps {
    library: LibraryData;
    onUiGoTo: (route: string) => void;
    onUseInspiration: (data: InspirationData) => void;
}

export function Inspirationscreen(props: InspirationscreenProps) {
    const {t, i18n} = useTranslation();

    const [activeInspirationIndex, setActiveInspirationIndex] = useState(0);

    const eventRef = useRef<HTMLDivElement>(null!);
    const leftRef = useRef<HTMLDivElement>(null!);
    const rightRef = useRef<HTMLDivElement>(null!);

    const inspiration = props.library.inspirations[activeInspirationIndex];

    const selectedBaseColor = props.library.baseColors.find((value) => {return value.id === inspiration.baseColorId;});
    const selectedBaseColorName = selectedBaseColor ? translate(selectedBaseColor.name, i18n) : '';
    const selectedContrastColor = props.library.contrastColors.find((value) => {return value.id === inspiration.contrastColorId;});
    const selectedContrastColorName = selectedContrastColor ? translate(selectedContrastColor.name, i18n) : '';

    let selectedStraussLogoName = '';
    let straussLogoIcon: ReactElement<any>|null = null;
    if(inspiration.straussLogo === StraussLogoType.Standard) {
        selectedStraussLogoName = t('logos.standard');
        straussLogoIcon = <IconStandard/>;
    } else if(inspiration.straussLogo === StraussLogoType.StitchedBlack) {
        selectedStraussLogoName = t('logos.stitchedBlack');
        straussLogoIcon = <IconBlack/>;
    } else if(inspiration.straussLogo === StraussLogoType.StitchedWhite) {
        selectedStraussLogoName = t('logos.stitchedWhite');
        straussLogoIcon = <IconWhite/>;
    }

    const previewProduct1 = props.library.products.find((product) => {return product.article === inspiration.article1});
    const previewProduct2 = props.library.products.find((product) => {return product.article === inspiration.article2});


    return (
        <Container fluid className={styles.container}>
            {
                props.library.baseColors.map((color, index) => {
                    return <div key={index} className={styles.background} style={{background: generateBackgroundStyle(color.bgColor, true), opacity: color.id === inspiration.baseColorId ? 1.0 : 0.0}}></div>
                })
            }

            <div className={styles.header}>
                <LogoText size={'big'} black={false}/>
                <h1>{t('inspirationscreen.header')}</h1>
                <button className={`${styles.buttonClose}`} onClick={(evt) => {props.onUiGoTo('editscreen');}}><LogoClose/></button>
            </div>
            <div className={styles.content} ref={eventRef}>
                <div className={styles.contentInfo}>
                    <div className={styles.genderInfo}>
                        {inspiration.gender === Gender.Female && <span><IconFemale/>{t('gender.female')}</span>}
                        {inspiration.gender === Gender.Male && <span><IconMale/>{t('gender.male')}</span>}
                    </div>
                    <div>
                        <span className={styles.infoBold}>{t('editscreen.baseColor')}:</span> <div className={styles.contentColor} style={{backgroundColor: selectedBaseColor ? selectedBaseColor.color : '#ffffff'}}></div><span>{selectedBaseColorName}</span>
                    </div>
                    <div>
                        <span className={styles.infoBold}>{t('editscreen.contrastColor')}:</span> <div className={styles.contentColor} style={{backgroundColor: selectedContrastColor ? selectedContrastColor.color : '#ffffff'}}></div><span>{selectedContrastColorName}</span>
                    </div>
                    <div>
                        <span className={styles.infoBold}>{t('editscreen.straussLogo')}:</span> {straussLogoIcon} <span>{selectedStraussLogoName}</span>
                    </div>
                </div>
                <div className={styles.contentMain}>
                    <div className={`${styles.contentButton} ${styles.contentButtonLeft}`} onClick={() => {if(activeInspirationIndex > 0) {setActiveInspirationIndex(activeInspirationIndex - 1);} else {setActiveInspirationIndex(props.library.inspirations.length - 1);}}}>
                        <ArrowLeft/>
                    </div>
                    <div className={`${styles.previewContainer} ${styles.previewContainerLeft}`} ref={leftRef}>
                        <BackgroundSprite1 className={styles.backgroundSplash} fill={generateBackgroundSplashContrastFill(selectedContrastColor)} />
                   </div>
                    <div className={`${styles.centerImageContainer}`}>
                        <div>
                            <img alt="First Basic Shirt" src={inspiration.centerImage1} />
                        </div>
                        <div>
                            <img alt="Second Basic Shirt"  src={inspiration.centerImage2} />
                        </div>
                    </div>
                    <div className={`${styles.previewContainer} ${styles.previewContainerRight}`} ref={rightRef}>
                        <BackgroundSprite2 className={styles.backgroundSplash} fill={generateBackgroundSplashBaseFill(selectedBaseColor)} />
                    </div>
                    {!isMobile && <button className={`${styles.buttonWhite}`} onClick={(evt) => {props.onUseInspiration(inspiration);}}>{t('inspirationscreen.use')}</button>}
                    <div className={styles.progress}><span>{activeInspirationIndex + 1}/{props.library.inspirations.length}</span><div><div style={{width: `${100 * activeInspirationIndex / (props.library.inspirations.length - 1)}%`}}></div></div></div>
                    <div className={`${styles.contentButton} ${styles.contentButtonRight}`} onClick={() => {if(activeInspirationIndex < props.library.inspirations.length - 1) {setActiveInspirationIndex(activeInspirationIndex + 1);} else {setActiveInspirationIndex(0);}}}>
                        <ArrowRight/>
                    </div>
                    {isMobile && <button className={`${styles.buttonWhite}`} onClick={(evt) => {props.onUseInspiration(inspiration);}}>{t('inspirationscreen.use')}</button>}
                </div>
                <PreviewContainer
                    className={styles.fullCanvas}
                    eventRef={eventRef}
                >
                    <PreviewContainerModel divRef={leftRef} library={props.library} disableZoom={isMobile} model={inspiration.article1} modelData={previewProduct1?.modelData} xOffset={0} baseColorId={inspiration.baseColorId} contrastColorId={inspiration.contrastColorId} straussLogo={inspiration.straussLogo} showPlanes={false} showSplashLogo={true} />
                    <PreviewContainerModel divRef={rightRef} library={props.library} disableZoom={isMobile} model={inspiration.article2} modelData={previewProduct2?.modelData} xOffset={0} baseColorId={inspiration.baseColorId} contrastColorId={inspiration.contrastColorId} straussLogo={inspiration.straussLogo} showPlanes={false} isRight={true} showSplashLogo={true} />
                </PreviewContainer>     
            </div>          
        </Container>
    );
};